if (!customElements.get('collapsible-tab')) {
	class CollapsibleTab extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.init();
		}

		disconnectedCallback() {
			this.destroy();
		}

		init() {
			const destroy = this.dataset.destroy === 'true';
			if (destroy) return;

			this.setDefaultData();

			if (!this.trigger || !this.content) return;

			this.attachEvents();
			const isInitiallyOpen = this.getAttribute('open') === 'true';

			if (isInitiallyOpen) {
				this.selected = true;
				this.isOpen = true;

				this.content.hidden = false;
				this.content.style.height = 'auto';

				this.classList.add(this.expandedClass, this.selectedClass);
				this.classList.remove(this.collapsedClass);

				this.setExpandedAria();
			} else {
				this.selected = false;
				this.isOpen = false;

				this.content.hidden = true;
				this.content.style.height = this.collapsedHeight;

				this.classList.add(this.collapsedClass);
				this.classList.remove(this.expandedClass, this.selectedClass);

				this.setCollapsedAria();
			}
		}

		transitionendEventName() {
			const el = document.createElement('div');
			const transitions = {
				transition: 'transitionend',
				OTransition: 'otransitionend',
				MozTransition: 'transitionend',
				WebkitTransition: 'webkitTransitionEnd',
			};

			for (const key in transitions) {
				if (Object.prototype.hasOwnProperty.call(transitions, key) && el.style[key] !== undefined) {
					return transitions[key];
				}
			}
			return 'transitionend';
		}

		expand() {
			this.content.hidden = false;
			const onEnd = (ev) => {
				if (ev.target !== this.content) return;
				this.content.removeEventListener(this.transitionendevent, onEnd);

				if (!this.isOpen) return;

				this.content.style.height = 'auto';
				this.setExpandedAria();
				this.classList.add(this.expandedClass);
				this.trySetTabIndex(this.content, 0);

				this.fire('tabOpened');
				window.ThemeEvents?.emit?.('ON_COLLAPSIBLE_TAB_OPENED', this);
			};

			this.content.addEventListener(this.transitionendevent, onEnd);

			this.isOpen = true;
			this.classList.remove(this.collapsedClass);
			this.content.style.height = this.content.scrollHeight + 'px';
		}

		collapse() {
			const onEnd = (ev) => {
				if (ev.target !== this.content) return;
				this.content.removeEventListener(this.transitionendevent, onEnd);

				if (this.isOpen) return;

				this.fire('elementClosed');
				window.ThemeEvents?.emit?.('ON_COLLAPSIBLE_TAB_CLOSED', this);

				this.setCollapsedAria();
				this.classList.add(this.collapsedClass);
				this.trySetTabIndex(this.content, -1);
				this.content.hidden = true;
			};

			this.content.addEventListener(this.transitionendevent, onEnd);
			this.isOpen = false;
			this.classList.remove(this.expandedClass);

			requestAnimationFrame(() => {
				this.content.style.height = this.content.scrollHeight + 'px';
				requestAnimationFrame(() => {
					this.content.style.height = this.collapsedHeight;
				});
			});
		}

		open() {
			if (this.oneAtATime) {
				const parent =
					this.closest('[data-collapsible-parent]') || this.closest('collection-tabs');

				if (parent) {
					const allItems = parent.querySelectorAll('collapsible-tab');
					allItems.forEach((item) => {
						if (item !== this && item.selected) item.close();
					});
				}
			}

			this.selected = true;
			this.classList.add(this.selectedClass);

			this.fire('elementSelected');
			window.ThemeEvents?.emit?.('ON_COLLAPSIBLE_TAB_SELECTED', this);

			this.setAttribute('open', 'true');
			this.expand();
		}

		close() {
			this.selected = false;
			this.classList.remove(this.selectedClass);

			this.fire('elementUnselected');
			window.ThemeEvents?.emit?.('ON_COLLAPSIBLE_TAB_UNSELECTED', this);

			this.removeAttribute('open');
			this.collapse();
		}

		toggle(event) {
			if (event) event.preventDefault();

			if (this.selected) {
				if (this.canCloseAll) this.close();
			} else {
				this.open();
			}
		}

		trySetTabIndex(el, index) {
			const selector = this.defaultElements.join(',');
			el.querySelectorAll(selector).forEach((e) => e.setAttribute('tabindex', index));
		}

		setExpandedAria() {
			this.trigger.setAttribute('aria-expanded', 'true');
			this.content.setAttribute('aria-hidden', 'false');
		}

		setCollapsedAria() {
			this.trigger.setAttribute('aria-expanded', 'false');
			this.content.setAttribute('aria-hidden', 'true');
		}

		attachEvents() {
			this.trigger.addEventListener('click', this._onTriggerClick);
		}

		setDefaultData() {
			this.events = {
				elementSelected: [],
				tabOpened: [],
				elementUnselected: [],
				elementClosed: [],
			};

			this._onTriggerClick = (event) => this.toggle(event);

			this.transitionendevent = this.transitionendEventName();
			this.expandedClass = 'is-expanded';
			this.selectedClass = 'is-selected';
			this.collapsedClass = 'is-collapsed';

			this.trigger = this.querySelector('[data-trigger]');
			this.content = this.querySelector('[data-content]');

			this.collapsedHeight = '0px';
			this.defaultElements = ['a', 'button', 'input:not(.focus-none)', '[data-trigger]'];

			this.oneAtATime = this.dataset.oneOpen ? this.dataset.oneOpen === 'true' : true;
			this.canCloseAll = !this.dataset.canCloseAll || this.dataset.canCloseAll === 'true';

			if (this.content) {
				this.content.style.overflow = 'hidden';
			}
		}

		fire(eventName) {
			(this.events[eventName] || []).forEach((cb) => cb(this));
		}

		on(eventName, cb) {
			if (!this.events[eventName]) return;
			this.events[eventName].push(cb);
		}

		destroy() {
			if (this.trigger && this._onTriggerClick) {
				this.trigger.removeEventListener('click', this._onTriggerClick);
			}

			if (this.content) {
				this.content.hidden = false;
				this.content.style.height = 'auto';
				this.content.style.transition = null;
				this.content.removeAttribute('aria-hidden');
			}

			this.classList.remove(this.expandedClass, this.collapsedClass, this.selectedClass);
			this.removeAttribute('open');

			this.selected = false;
			this.isOpen = false;
		}
	}

	customElements.define('collapsible-tab', CollapsibleTab);
}
